import type { Workspace } from '@core/entities/Workspace';
import type { WorkspaceMember } from '@core/entities/WorkspaceMember';
import type { Invitation } from '@core/entities/Invitation';
import type { User } from '@core/entities/User';
import type {
  CreateWorkspacePayload,
  UpdateWorkspacePayload,
  WorkspaceRepository,
} from '@core/repositories/WorkspaceRepository';
import { httpClient } from '@infrastructure/http/httpClient';
import { toAppError } from '@infrastructure/http/errorMapper';
import { toUser, type UserEnvelope } from '@infrastructure/mappers/toUser';
import {
  toRequestSettings,
  toRequestSettingsPayload,
  type RequestSettingsResponse,
} from '@infrastructure/mappers/requestSettingsMapper';

interface WorkspaceResponse {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  settings?: RequestSettingsResponse | null;
  type: 'personal' | 'team';
  visibility: 'private' | 'team' | 'public';
  avatar_url: string | null;
  is_archived: boolean;
  archived_at: string | null;
  owner: { id: string; name: string; email: string };
  team_id: string | null;
  member_count: number | null;
  abilities: {
    view: boolean;
    update: boolean;
    delete: boolean;
    manage_members: boolean;
    manage_settings: boolean;
    transfer: boolean;
  };
  created_at: string;
  updated_at: string;
}

interface WorkspaceMemberResponse {
  id: string;
  user: { id: string; name: string; email: string; avatar_url: string | null };
  role: { id: string; name: string; slug: string };
  status: string;
  is_owner: boolean;
  joined_at: string | null;
}

interface InvitationResponse {
  id: string;
  email: string;
  role: { id: string; name: string; slug: string };
  status: string;
  invited_by: { id: string; name: string };
  workspace?: { id: string; name: string };
  expires_at: string;
  created_at: string;
}

const toWorkspace = (payload: WorkspaceResponse): Workspace => ({
  id: payload.id,
  name: payload.name,
  slug: payload.slug,
  description: payload.description,
  settings: toRequestSettings(payload.settings),
  type: payload.type,
  visibility: payload.visibility,
  avatarUrl: payload.avatar_url,
  isArchived: payload.is_archived,
  archivedAt: payload.archived_at,
  owner: payload.owner,
  teamId: payload.team_id,
  memberCount: payload.member_count,
  abilities: {
    view: payload.abilities.view,
    update: payload.abilities.update,
    delete: payload.abilities.delete,
    manageMembers: payload.abilities.manage_members,
    manageSettings: payload.abilities.manage_settings,
    transfer: payload.abilities.transfer,
  },
  createdAt: payload.created_at,
  updatedAt: payload.updated_at,
});

const toMember = (payload: WorkspaceMemberResponse): WorkspaceMember => ({
  id: payload.id,
  user: {
    id: payload.user.id,
    name: payload.user.name,
    email: payload.user.email,
    avatarUrl: payload.user.avatar_url,
  },
  role: payload.role,
  status: payload.status,
  isOwner: payload.is_owner,
  joinedAt: payload.joined_at,
});

const toInvitation = (payload: InvitationResponse): Invitation => ({
  id: payload.id,
  email: payload.email,
  role: payload.role,
  status: payload.status,
  invitedBy: payload.invited_by,
  workspace: payload.workspace,
  expiresAt: payload.expires_at,
  createdAt: payload.created_at,
});

export class WorkspaceRepositoryImpl implements WorkspaceRepository {
  async list(): Promise<Workspace[]> {
    try {
      const response = await httpClient.get<{ data: WorkspaceResponse[] }>('/workspaces');
      return response.data.data.map(toWorkspace);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async get(id: string): Promise<Workspace> {
    try {
      const response = await httpClient.get<{ data: WorkspaceResponse }>(`/workspaces/${id}`);
      return toWorkspace(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async create(payload: CreateWorkspacePayload): Promise<Workspace> {
    try {
      const response = await httpClient.post<{ data: WorkspaceResponse }>('/workspaces', {
        name: payload.name,
        description: payload.description,
        visibility: payload.visibility,
        team_uuid: payload.teamId,
      });
      return toWorkspace(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async update(id: string, payload: UpdateWorkspacePayload): Promise<Workspace> {
    try {
      const response = await httpClient.put<{ data: WorkspaceResponse }>(`/workspaces/${id}`, {
        ...payload,
        settings:
          payload.settings !== undefined ? toRequestSettingsPayload(payload.settings) : undefined,
      });
      return toWorkspace(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async archive(id: string): Promise<Workspace> {
    try {
      const response = await httpClient.post<{ data: WorkspaceResponse }>(
        `/workspaces/${id}/archive`,
      );
      return toWorkspace(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async restore(id: string): Promise<Workspace> {
    try {
      const response = await httpClient.post<{ data: WorkspaceResponse }>(
        `/workspaces/${id}/restore`,
      );
      return toWorkspace(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async transfer(id: string, userId: string): Promise<Workspace> {
    try {
      const response = await httpClient.post<{ data: WorkspaceResponse }>(
        `/workspaces/${id}/transfer`,
        {
          user_uuid: userId,
        },
      );
      return toWorkspace(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async switchTo(id: string): Promise<User> {
    try {
      const response = await httpClient.post<UserEnvelope>(`/workspaces/${id}/switch`);
      return toUser(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await httpClient.delete(`/workspaces/${id}`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async listMembers(workspaceId: string): Promise<WorkspaceMember[]> {
    try {
      const response = await httpClient.get<{ data: WorkspaceMemberResponse[] }>(
        `/workspaces/${workspaceId}/members`,
      );
      return response.data.data.map(toMember);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async updateMemberRole(
    workspaceId: string,
    memberId: string,
    roleId: string,
  ): Promise<WorkspaceMember> {
    try {
      const response = await httpClient.put<{ data: WorkspaceMemberResponse }>(
        `/workspaces/${workspaceId}/members/${memberId}`,
        { role_uuid: roleId },
      );
      return toMember(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async removeMember(workspaceId: string, memberId: string): Promise<void> {
    try {
      await httpClient.delete(`/workspaces/${workspaceId}/members/${memberId}`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async leave(workspaceId: string): Promise<void> {
    try {
      await httpClient.post(`/workspaces/${workspaceId}/leave`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async listInvitations(workspaceId: string): Promise<Invitation[]> {
    try {
      const response = await httpClient.get<{ data: InvitationResponse[] }>(
        `/workspaces/${workspaceId}/invitations`,
      );
      return response.data.data.map(toInvitation);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async inviteMember(workspaceId: string, email: string, roleId: string): Promise<Invitation> {
    try {
      const response = await httpClient.post<{ data: InvitationResponse }>(
        `/workspaces/${workspaceId}/invitations`,
        { email, role_uuid: roleId },
      );
      return toInvitation(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async revokeInvitation(workspaceId: string, invitationId: string): Promise<void> {
    try {
      await httpClient.delete(`/workspaces/${workspaceId}/invitations/${invitationId}`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async getInvitation(token: string): Promise<Invitation> {
    try {
      const response = await httpClient.get<{ data: InvitationResponse }>(`/invitations/${token}`);
      return toInvitation(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async acceptInvitation(token: string): Promise<WorkspaceMember> {
    try {
      const response = await httpClient.post<{ data: WorkspaceMemberResponse }>(
        `/invitations/${token}/accept`,
      );
      return toMember(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async declineInvitation(token: string): Promise<void> {
    try {
      await httpClient.post(`/invitations/${token}/decline`);
    } catch (error) {
      throw toAppError(error);
    }
  }
}
